import center from "@turf/center"
import { ChevronRight, Search, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { IParcel } from "@/types/explore"
import { formatDistanceLabel } from "@/lib/explore/utils/format-distance"
import { formatNumber } from "@/lib/explore/utils/format-helpers"

interface ParcelResultItemProps {
  parcel: IParcel
  highlighted?: boolean
  badgeColor?: "primary" | "gray"
  showDistance?: boolean
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const ParcelResultItem = ({
  parcel,
  highlighted,
  badgeColor = "gray",
  showDistance,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: ParcelResultItemProps) => {
  const badgeClass =
    badgeColor === "primary"
      ? "bg-primary/[0.08] text-primary group-hover:bg-primary/[0.15]"
      : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`group w-full cursor-pointer flex items-center gap-2.5 px-2.5 py-2 text-left rounded-lg transition-colors ${
        highlighted ? "bg-primary/[0.06]" : "hover:bg-gray-50"
      }`}
    >
      <div
        className={`flex-shrink-0 w-8 h-8 ${badgeClass} rounded-lg flex items-center justify-center text-sm font-bold transition-colors`}
      >
        {parcel.properties?.section}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">
          <span className="font-semibold text-gray-800">
            {parcel.properties?.section} {parcel.properties?.numero}
          </span>
          <span className="text-gray-400 ml-1.5">{parcel.properties?.code_insee}</span>
          <span className="text-gray-400 ml-0.5">{"\u00B7"}</span>
          <span className="text-gray-500 ml-0.5">{parcel.properties?.nom_com}</span>
        </div>
        <div className="text-xs text-gray-400 mt-0.5 truncate">
          {formatNumber(Math.round(parcel.properties?.contenance))} m{"\u00B2"}
          {showDistance && parcel.distanceKm != null && ` \u00B7 ${formatDistanceLabel(parcel.distanceKm)}`}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary transition-colors shrink-0" />
    </button>
  )
}

interface ParcelResultsPanelProps {
  searchResults: IParcel[]
  showSearchResults: boolean
  showFilterResults: boolean
  showNoResults: boolean
  highlightedIndex: number
  parcelFilterResults: IParcel[]
  onSelectSearchResult: (parcel: IParcel) => void
  onSelectFilterResult: (parcel: IParcel) => void
  onHoverResult?: (lngLat: [number, number]) => void
  onLeaveResult?: () => void
  onResetFilters: () => void
  onClose?: () => void
}

export const ParcelResultsPanel = ({
  searchResults,
  showSearchResults,
  showFilterResults,
  showNoResults,
  highlightedIndex,
  parcelFilterResults,
  onSelectSearchResult,
  onSelectFilterResult,
  onHoverResult,
  onLeaveResult,
  onResetFilters,
  onClose,
}: ParcelResultsPanelProps) => (
  <div className="bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl w-72 max-h-80 flex flex-col">
    {showNoResults && (
      <div className="px-2 py-6 text-center">
        <Search className="w-8 h-8 mx-auto text-gray-300 mb-2" />
        <div className="text-sm text-gray-400">Aucun resultat</div>
      </div>
    )}

    {/* Headers outside scroll */}
    {showSearchResults && (
      <div className="px-3 pt-2.5 pb-1 shrink-0">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {searchResults.length} resultat{searchResults.length > 1 ? "s" : ""} par reference
        </div>
      </div>
    )}
    {!showSearchResults && showFilterResults && (
      <div className="px-3 pt-2.5 pb-1 shrink-0 flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          {parcelFilterResults.length > 50
            ? `50 / ${parcelFilterResults.length} parcelles sur la carte`
            : `${parcelFilterResults.length} parcelle${parcelFilterResults.length > 1 ? "s" : ""} sur la carte`}
        </div>
        {onClose && (
          <Button
            onClick={onClose}
            variant="ghost"
            size="icon-xs"
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X />
          </Button>
        )}
      </div>
    )}

    {/* Scrollable results */}
    <div className="overflow-y-auto p-1.5 pt-0">
      {showSearchResults && (
        <div className="space-y-0.5">
          {searchResults.map((result, i) => (
            <ParcelResultItem
              key={`search-${result.properties?.idu || i}`}
              parcel={result}
              highlighted={i === highlightedIndex}
              badgeColor="primary"
              showDistance
              onClick={() => {
                onSelectSearchResult(result)
                onResetFilters()
              }}
              onMouseEnter={() => result.center && onHoverResult?.(result.center)}
              onMouseLeave={() => onLeaveResult?.()}
            />
          ))}
        </div>
      )}

      {showFilterResults && (
        <>
          {showSearchResults && (
            <div className="px-2.5 pt-2 pb-1">
              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                {parcelFilterResults.length > 50
                  ? `50 / ${parcelFilterResults.length} parcelles sur la carte`
                  : `${parcelFilterResults.length} parcelle${parcelFilterResults.length > 1 ? "s" : ""} sur la carte`}
              </div>
            </div>
          )}
          <div className="space-y-0.5">
            {parcelFilterResults.slice(0, 50).map((parcel, i) => (
              <ParcelResultItem
                key={`filter-${parcel.properties?.idu || i}`}
                parcel={parcel}
                badgeColor="gray"
                onClick={() => {
                  onSelectFilterResult(parcel)
                }}
                onMouseEnter={() => {
                  if (parcel.geometry) {
                    const c = center(parcel.geometry).geometry.coordinates as [number, number]
                    onHoverResult?.(c)
                  }
                }}
                onMouseLeave={() => onLeaveResult?.()}
              />
            ))}
          </div>
        </>
      )}
    </div>
  </div>
)
